/** Suite workloads nobody switched on — the downgrade shortlist (L1).
 *
 *  A seat can be fully assigned, fully paid for, and still not be using the
 *  thing that made the suite expensive. `assignedPlans` says which workloads
 *  inside a licence are actually provisioned, and it was already arriving on
 *  every user page for the reclaim maths — so this panel costs no Graph call,
 *  no extra permission, and no new payload.
 *
 *  **Form (design rules §2).** Comparing magnitudes across named categories is
 *  a sorted horizontal bar, not a table; the full list — including the rows a
 *  bar cannot express — is the chart's table twin, one click away.
 *
 *  **`null` is not `0`.** A row whose plan state could not be read carries
 *  `inactive_users: null`. It is kept out of the bars (an unknown has no
 *  length) and called out in its own alert, because "no seat has this switched
 *  off" and "we could not determine whether any seat has this switched off"
 *  are opposite findings that must never render alike.
 *
 *  **Privacy.** Aggregate counts only, and the backend drops any SKU with too
 *  few holders to report on without pointing at an individual. Nothing here is
 *  person-level, at any role — that stays the admin-gated accounts page's job.
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { Alert, EmptyState } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { ServicePlanWasteRow } from "@/lib/api";

import { formatPct, formatPlanOffSeats, formatSeats } from "./licence-format";

/** How many plans the ranking shows — the rest are behind View data. */
const TOP_LIMIT = 10;

/** The backend rebuilds its snapshot every 15 minutes; flag anything older. */
const STALE_AFTER_MS = 15 * 60_000;

/** One series, one colour — never a ramp over nominal categories (§3.1). */
const PLAN_CONFIG: ChartConfig = {
  inactive_users: { label: "Seats without the workload", color: "var(--norican-accent)" },
};

interface ServicePlanWasteProps {
  rows: ServicePlanWasteRow[];
  generatedAt: string | null | undefined;
}

export function ServicePlanWaste({ rows, generatedAt }: ServicePlanWasteProps) {
  if (rows.length === 0) {
    /* Nothing to render, and "every workload is provisioned" would be the
       wrong thing to claim: an empty list also happens when every SKU is
       below the reporting floor. Saying nothing beats saying all-clear. */
    return null;
  }

  const unreadable = rows.filter((row) => row.inactive_users == null);
  const ranked = rows.filter((row) => (row.inactive_users ?? 0) > 0);
  const barRows = ranked.slice(0, TOP_LIMIT).map((row) => ({
    ...row,
    // The plan name repeats across suites, so the category carries its SKU.
    label: `${row.name} · ${row.sku_part_number}`,
  }));

  return (
    <ChartCard<ServicePlanWasteRow>
      title="Suite workloads switched off"
      caption={
        ranked.length > TOP_LIMIT
          ? `Top ${TOP_LIMIT} of ${ranked.length} workloads with seats not provisioned`
          : "Key workloads not provisioned on the seats that hold them"
      }
      since={generatedAt}
      staleAfterMs={STALE_AFTER_MS}
      tableTwin={{
        id: "service-plan-waste",
        ariaLabel: "Key service plans per SKU, with the seats not provisioned for each",
        data: rows,
        getRowId: (row: ServicePlanWasteRow) => `${row.sku_id}:${row.service_plan_name}`,
        columns: [
          { id: "sku", header: "SKU", accessor: (row) => row.sku_name },
          {
            id: "plan",
            header: "Workload",
            accessor: (row) => row.name,
            cell: (row) => (
              <div className="flex flex-col">
                <span className="text-fg font-medium">{row.name}</span>
                <span className="text-fg-muted text-xs">{row.service_plan_name}</span>
              </div>
            ),
          },
          {
            id: "off",
            header: "Switched off",
            accessor: (row) => row.inactive_users,
            align: "end",
            cell: (row) => <span className="tabular-nums">{formatPlanOffSeats(row)}</span>,
          },
          {
            id: "share",
            header: "Share of seats",
            accessor: (row) => row.inactive_pct,
            align: "end",
            cell: (row) => <span className="tabular-nums">{formatPct(row.inactive_pct)}</span>,
          },
          {
            id: "seats",
            header: "Seats holding the SKU",
            accessor: (row) => row.licensed_users,
            align: "end",
            cell: (row) => <span className="tabular-nums">{formatSeats(row.licensed_users)}</span>,
          },
        ],
      }}
    >
      {unreadable.length > 0 && (
        /* An unreadable count is a finding about the data, not a clean sheet.
           It is stated before the chart precisely because the chart cannot
           show it. */
        <Alert tone="warning" title="Some workloads could not be determined">
          Microsoft Graph returned no service-plan detail for the seats behind{" "}
          {unreadable.length === 1 ? "one workload" : `${unreadable.length} workloads`}, so whether
          they are switched on is unknown — not zero. They are listed under View data with an em
          dash rather than a count.
        </Alert>
      )}

      {barRows.length === 0 ? (
        <EmptyState
          size="sm"
          title={
            unreadable.length === rows.length
              ? "Nothing could be measured"
              : "Every reported workload is switched on"
          }
          description={
            unreadable.length === rows.length
              ? "No seat carried readable service-plan detail, so this is an unknown rather than a clean result."
              : "On the SKUs large enough to report on, every key workload is provisioned for every seat holding it."
          }
        />
      ) : (
        <Chart.Container
          config={PLAN_CONFIG}
          className="h-72"
          aria-label={`Seats without the workload provisioned, for the ${barRows.length} worst key service plans`}
        >
          <Chart.BarChart
            data={barRows}
            layout="vertical"
            margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
          >
            <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
            <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
            <Chart.YAxis {...Chart.axisProps} type="category" dataKey="label" width={240} />
            <Chart.Tooltip
              cursor={{ fill: "var(--norican-surface-hover)" }}
              content={
                <Chart.TooltipContent
                  formatter={(value) => `${formatSeats(Number(value))} seats without it`}
                />
              }
            />
            <Chart.Bar
              {...Chart.seriesDefaults}
              dataKey="inactive_users"
              fill="var(--color-inactive_users)"
              radius={[0, 3, 3, 0]}
            />
          </Chart.BarChart>
        </Chart.Container>
      )}
    </ChartCard>
  );
}
